import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useLoaderData } from "react-router-dom";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { getScoreTrendLabel, mapPlaylistPerspective } from "../components/HelperFunctions";
import {
    useFetchData,
    useSetCategoriesOnPlaylistPerspectiveChange,
    useSetDataTypeOnStationChange,
    useSetPlaylistPerspectiveOnCategoriesChange,
    useSongCategoriesWithPerspectives,
    useStationsAvailableInModule,
} from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DataTypePicker from "../components/Pickers/DataTypePicker";
import PlaylistPerspectivePicker from "../components/Pickers/PlaylistPerspectivePicker";
import SongCategoriesPicker from "../components/Pickers/SongCategoriesPicker";
import StationPicker from "../components/Pickers/StationPicker";
import RATooltip from "../components/RATooltip";
import { useMusicTestTrendModal } from "../components/TrendOverTime";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, InputSelection, ModulePage, SearchWithDownloadRow, SoleDescriptionRow, VirtualizedWindowContainer } from "../components/pageSections";
import PlaylistAutopilotHeader from "../components/playlistAutopilot/PlaylistAutopilotHeader";
import PlaylistAutopilotRow from "../components/playlistAutopilot/PlaylistAutopilotRow";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";
import moment from "moment/min/moment-with-locales";
import { axiosInstance } from "../auth/Auth";
import PlaylistAutopilotImportModal from "../components/playlistAutopilot/PlaylistAutopilotImportModal";
import { Upload } from "react-bootstrap-icons";

export default function PlaylistAutopilot() {
    const moduleName = "playlistAutopilot";
    const { customPlaylistAutopilotRadios } = useLoaderData();

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName);
    const [loading, errorCode, data, fetchData, setData] = useFetchData();
    const [filterData, setFilterData] = useState({
        playlistPerspective: [],
        categories: [],
        showMoves: "changes",
        searchString: "",
    });
    const [customResults, setCustomResults] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    const stationsAvailable = useStationsAvailableInModule(x => x.module_id === 15);
    const categoriesStation = useMemo(() => (customResults ? data?.radio_id : null), [customResults, data?.radio_id]);
    const perspectivesWithCategories = useSongCategoriesWithPerspectives(categoriesStation);

    const tableContainerRef = useRef(null);
    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();
    const { setTrendSong, MusicTestTrendModalComponent } = useMusicTestTrendModal();

    useSetDataTypeOnStationChange(setFormData, formData.station);
    useSetCategoriesOnPlaylistPerspectiveChange(setFilterData, filterData.playlistPerspective, perspectivesWithCategories);
    useSetPlaylistPerspectiveOnCategoriesChange(setFilterData, filterData.categories, perspectivesWithCategories);

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data.data;
            if (filterData.showMoves === "changes") {
                filteredData = filteredData?.filter(item => item.song_category !== item.song_category_move);
            }
            if (filterData.categories.length) {
                filteredData = filteredData.filter(x => filterData.categories.some(c => x.song_category === c.code || x.song_category_move === c.code));
            }
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.song.artist.toLowerCase();
                    const title = x.song.title.toLowerCase();
                    const year = x.song.release_year?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || year === exact;
                            }

                            return artist.includes(term) || title.includes(term) || year.startsWith(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData]);

    useEffect(() => {
        if (perspectivesWithCategories?.length) {
            setFilterData(current => ({
                ...current,
                playlistPerspective: JSON.stringify(perspectivesWithCategories[0].categories.map(x => x.code)),
            }));
        }
    }, [perspectivesWithCategories]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(dataMemo);

    const resultCount = useMemo(() => {
        if (sortedItems) return sortedItems.length;
        return 0;
    }, [sortedItems]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const custom = (await customPlaylistAutopilotRadios).data.includes(formData.station[0]?.radio_id) ? "custom/" : "";
        const url = `/playlist_autopilot/${custom}${formData.station[0]?.radio_id}?data_type=${formData.dataType}`;
        await fetchData(url);
        setCustomResults(!!custom);
    }

    async function handleCategoryChange(newCategory, itemId) {
        const orignalData = { ...data };
        try {
            setData(prev => ({
                ...prev,
                data: prev.data.map(item => (item.song.item_id === itemId ? { ...item, song_category_manual_move: newCategory } : item)),
            }));
            await axiosInstance.patch(`playlist_autopilot/custom/${data.radio_id}/${itemId}/${encodeURIComponent(newCategory)}?data_type=${data.data_type}`);
        } catch (error) {
            setData(orignalData);
        }
    }

    const columns = useMemo(() => {
        if (customResults) {
            return [
                { header: "Title", value: x => x.song.title },
                { header: "Artist", value: x => x.song.artist },
                { header: "Client ID", value: x => x.song.song_id },
                { header: "RA ID", value: x => x.song.item_id },
                { header: "Category Move", value: x => x.song_category_manual_move ?? x.song_category_move },
                { header: "Score Current", value: x => x.song_score },
                { header: "Score Long-Term", value: x => x.song_score_long_term },
                { header: "Score Trend", value: x => getScoreTrendLabel(x.song_score_trend).toUpperCase() },
                { header: "Burn", value: x => x.burn },
                { header: "Exposure", value: x => x.exposure },
            ];
        }

        return [
            { header: "Category Name", value: x => x.song_category },
            { header: "Category Move", value: x => x.song_category_move },
            { header: "Artist", value: x => x.song.artist },
            { header: "Title", value: x => x.song.title },
            { header: "Release Year", value: x => x.song.release_year },
            { header: "Spins", value: x => x.spins },
            { header: "Total Spins", value: x => x.total_spins },
            { header: "Score Current", value: x => x.song_score },
            { header: "Score Long-Term", value: x => x.song_score_long_term },
            { header: "Score Trend", value: x => getScoreTrendLabel(x.song_score_trend).toUpperCase() },
            { header: "Burn", value: x => x.burn },
            { header: "Exposure", value: x => x.exposure },
        ];
    }, [customResults]);

    const csvHeaders = useMemo(() => columns.map(c => c.header), [columns]);
    const csvData = useMemo(() => sortedItems?.map(x => columns.map(c => c.value(x))), [sortedItems, columns]);

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 54, []),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].song.item_id, [sortedItems]),
        overscan: 20,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <ModulePage>
            <ModulePage.Title className="d-flex flex-column flex-md-row justify-content-between col-12">
                <div className="text-start">Playlist Autopilot</div>
                <Button
                    variant="ra-yellow"
                    className="d-flex align-self-md-center justify-content-center mt-2 mt-md-0 col-6 col-md-auto align-items-center column-gap-1"
                    onClick={() => setShowImportModal(true)}>
                    <Upload size={20} />
                    Import Playlist
                </Button>
            </ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length}>
                <InputSelection.Main>
                    {!stationsAvailable && (
                        <p>
                            Contact your RA rep or{" "}
                            <a
                                className="text-dark fw-bold"
                                href="mailto:training@radioanalyzer.com?subject=Upgrade%20to%20Playlist%20Autopilot"
                                target="blank">
                                Click Here
                            </a>{" "}
                            to upgrade...
                        </p>
                    )}
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        moduleFilterFunction={x => x.module_id === 15}
                        filterFunction={x => !x.mtt_only}
                        loadOnChange={loadFormData}
                    />
                    <DataTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Playlist Autopilot"}
                        whatList={[
                            "Playlist meeting prep in seconds, based on your categories, rotations, and guidelines",
                            "Optimal category moves are displayed based on song score – you only need to approve or discard the changes",
                            "Automated export to your music planning system coming soon!",
                        ]}
                        knowList={[
                            {
                                text: "Move suggestions are based on song performance, trends, and age. Songs with low weekly spin counts (less than 4) will automatically look at monthly averages and trends when deciding where to move.",
                            },
                            { text: "One time setup (we can help with this) is required for optimal results" },
                            { text: "Choose between your playlist, your back pool, or any category(ies) of your choosing" },
                            { text: "Click on trend to see the recent song development of spins and scores" },
                        ]}
                        relatedLinks={[
                            {
                                title: "Introducing Playlist Autopilot: The Future of Music Curation",
                                url: "https://radioanalyzer.com/blog/introducing-playlist-autopilot-the-future-of-music-curation",
                            },
                            {
                                title: "Playlist Autopilot: We Are Flicking the Switch to 'On'",
                                url: "https://radioanalyzer.com/blog/playlist-autopilot-we-are-flicking-the-switch-to-on",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/XNhTC6nnYSw?si=kqyWYKJJJmupIAW1"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <SoleDescriptionRow>
                            <DataDescription>
                                <DataDescription.Station radioId={data?.radio_id} />
                                {customResults ? (
                                    <DataDescription.Simple label="Generated date" value={moment(data?.generated_at).format("L LT")} />
                                ) : (
                                    <DataDescription.DateRange startDate={data.basis_week} />
                                )}
                                <DataDescription.Simple
                                    label="Time Frame"
                                    value={data.time_frame === 0 ? "Last Week" : data.time_frame === 1 ? "Last Month" : "Last Quarter"}
                                />
                                <DataDescription.Simple
                                    label="Categories"
                                    value={mapPlaylistPerspective(perspectivesWithCategories, filterData.categories)?.perspective ?? "Custom"}
                                />
                                <DataDescription.Simple label="Results" value={resultCount} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <Stack direction="horizontal" gap={3} className=" mb-2 flex-wrap row-gap-1 align-items-start">
                            <PlaylistPerspectivePicker
                                formData={filterData}
                                setFormData={setFilterData}
                                playlistPerspectives={perspectivesWithCategories}
                                name={moduleName}
                            />
                            <SongCategoriesPicker
                                formData={filterData}
                                setFormData={setFilterData}
                                options={perspectivesWithCategories[0]?.categories}
                                name={moduleName}
                            />
                            <Form.Group as={Col} className="d-flex flex-column col-12 col-xl-auto">
                                <Form.Label className=" d-inline-flex">
                                    Show Categories
                                    <RATooltip className="ms-1">
                                        Selecting 'Changes' will only display results with song categories that are suggested to be moved. Choosing 'All' will
                                        show categories that should be changed and those that should remain the same.
                                    </RATooltip>
                                </Form.Label>
                                <ToggleButtonGroup
                                    type="radio"
                                    name="categories"
                                    value={filterData.showMoves}
                                    onChange={value => setFilterData({ ...filterData, showMoves: value })}
                                    className="ra-switcher">
                                    <ToggleButton id={`${moduleName}-plap-changes-only`} variant={"ra-dark-blue-outline"} value={"changes"}>
                                        Changes
                                    </ToggleButton>
                                    <ToggleButton id={`${moduleName}-plap-changes-all`} variant={"ra-dark-blue-outline"} value={"all"}>
                                        All
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Form.Group>
                        </Stack>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            csvHeaders={csvHeaders}
                            data={csvData}
                            filename={`Playlist Autopilot ${customResults ? "Custom " : ""}- ${data.basis_week}`}
                        />
                        <div className=" overflow-x-auto">
                            <PlaylistAutopilotHeader sortConfig={sortConfig} requestSort={requestSort} />
                            <VirtualizedWindowContainer className="playlist-autopilot-mw" containerRef={tableContainerRef} virtualizer={virtualizer}>
                                {virtualizer.getVirtualItems().map(virtualRow => {
                                    const item = sortedItems[virtualRow.index];
                                    if (!item) return <EmptyRow key={virtualRow.key} text="No songs" />;
                                    return (
                                        <PlaylistAutopilotRow
                                            key={virtualRow.key}
                                            item={item}
                                            onChange={customResults && handleCategoryChange}
                                            virtualizer={virtualizer}
                                            virtualRow={virtualRow}
                                            setCatalogueSong={setCatalogueSong}
                                            setTrendSong={setTrendSong}
                                            radioId={customResults ? data.radio_id : null}
                                        />
                                    );
                                })}
                            </VirtualizedWindowContainer>
                        </div>
                    </>
                )}
                <PlaylistAutopilotImportModal show={showImportModal} onClose={() => setShowImportModal(false)} />
                <MusicTestTrendModalComponent radioId={data?.radio_id} dataType={data?.data_type} />
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
